import { Component, Injector, OnInit, Signal, computed, inject, runInInjectionContext } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router, ActivatedRoute } from "@angular/router";
import { Observable, of, EMPTY } from "rxjs";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { map, switchMap, filter, withLatestFrom, publishReplay, refCount, tap, catchError } from "rxjs/operators";
import { toSignal } from "@angular/core/rxjs-interop";

import { ApplicationState } from "../../shared/state/types";
import { Task } from "../../shared/types";
import { TasksModel } from "../tasks.model";
import { TaskFormResult } from "../types/task-form-result";
import { SimpleToastService } from "../../shared/modules";
import { LanguagePipe } from "../../shared/pipes";
import { selectWoundCareTreeItemById } from "../../shared/state/selectors/wound-care-tree.selectors";
import { DialogComponent } from "../../shared/components/dialogs/dialog/dialog.component";

@Component({
	selector: "ic-tasks-detail",
	templateUrl: "tasks-detail.component.html",
	standalone: false,
})
export class TasksDetailComponent implements OnInit {
	private readonly tasksModel = inject(TasksModel);
	private readonly translateService = inject(TranslateService);
	private readonly router = inject(Router);
	private readonly route = inject(ActivatedRoute);
	private readonly store = inject<Store<ApplicationState>>(Store);
	private readonly toast = inject(SimpleToastService);

	// @ts-expect-error TS2564
	task$: Observable<Task>;
	// @ts-expect-error TS2564
	form$: Observable<any>;
	// @ts-expect-error TS2564
	loading$: Observable<boolean>;

	private readonly languagePipe = inject(LanguagePipe);
	private readonly injector = inject(Injector);

	private ref: any;
	private defaultConfig: any;
	// @ts-expect-error TS2564
	private task: Signal<Task>;

	protected toolbarTitle = computed(() => {
		const task = this.task();
		if (task) {
			if (task.recordType === "WOUNDCARE") {
				return this.translateService.instant(
					task.executed ? "wound_care.wound_care_observation" : "wound_care.add_wound_care_observation",
				);
			} else {
				return this.languagePipe.transform(task.name);
			}
		} else {
			return "";
		}
	});

	protected breadcrumbs = computed<string>(() => {
		const path = this.task()?.woundCare?.protocol?.path;
		const rootBranch = this.store.selectSignal(selectWoundCareTreeItemById(path[0]))();
		// @ts-expect-error TS7034
		const childBranchNames = [];
		// @ts-expect-error TS7034
		let currentBranch;
		if (path?.length && rootBranch) {
			currentBranch = rootBranch;
			// @ts-expect-error TS7006
			path.slice(1).forEach((branchId) => {
				// @ts-expect-error TS7005
				const childBranch = currentBranch.children[branchId];
				if (childBranch) {
					childBranchNames.push(this.languagePipe.transform(childBranch.name));
					currentBranch = childBranch;
				}
			});
		}
		// @ts-expect-error TS7005
		return childBranchNames.join(" > ");
	});

	protected dialog = inject(MatDialog);

	async goBack() {
		const { patient } = this.route.snapshot.params;

		return this.router.navigate([patient, "overview"]);
	}

	onCreate(result: TaskFormResult) {
		const { patient, id } = this.route.snapshot.params;

		if (result.evaluationForm?.observation?.healed) {
			const dialogRef = this.dialog.open(DialogComponent, {
				data: {
					content: "general.dialogs.healed.body",
					title: "general.warning",
					cancelButtonText: "general.cancel",
					confirmButtonText: "general.confirm",
				},
			});

			return dialogRef
				.afterClosed()
				.pipe(
					filter(Boolean),
					switchMap(() =>
						this.tasksModel.updateTask(
							patient,
							id,
							this.ref ? { ...result, form: this.ref.form } : result,
							this.task(),
						),
					),
					tap(() => this.goBack()),
				)
				.subscribe();
		} else {
			this.tasksModel
				.updateTask(patient, id, this.ref ? { ...result, form: this.ref.form } : result, this.task())
				.pipe(tap(() => this.goBack()))
				.subscribe();
		}
	}

	ngOnInit() {
		this.defaultConfig = {
			form: [],
		};

		const task$ = this.route.paramMap.pipe(
			switchMap((params) => {
				const patient = params.get("patient");
				const taskId = params.get("id");

				return this.tasksModel.getTask(patient, taskId).pipe(
					map(({ task, alreadyExecutedByOtherUser }) => {
						if (alreadyExecutedByOtherUser) {
							this.toast.info(
								this.translateService.instant("tasks.already_executed", {
									// @ts-expect-error TS18048
									user: `${task.user.name} ${task.user.firstName}`,
								}),
								{ priority: 10 },
							);
						}

						return task;
					}),
					catchError(() => {
						this.navigateToNotFound();

						return EMPTY;
					}),
				);
			}),
			filter<Task>(Boolean),
			publishReplay(1),
			refCount(),
		);

		this.loading$ = this.store
			.select((state) => state.ui.tasks.loading)
			.pipe(
				withLatestFrom(task$),
				map(([, task]) => !task),
			);

		this.form$ = task$.pipe(
			filter<Task>(Boolean),
			switchMap((task) => {
				if (!task.form) {
					return of(this.defaultConfig);
				}

				return this.store
					.select((state) => state.data.evaluationforms && state.data.evaluationforms.forms)
					.pipe(
						map((forms) => {
							if (typeof task.form === "string") {
								return [...forms].find((form) => form.id === task.form && form.tag === "latest");
							}

							return [...forms].find((form) => form.id === task.form.id && form.version === task.form.version);
						}),
						map((config) => config || this.defaultConfig),
					);
			}),
			// TODO Remove this after we removed the `notes` controls from the back-end evaluation forms
			// @ts-expect-error TS7006
			map((data: any) => data && data.form.filter((f) => f.name !== "notes" && f.name !== "note")),
		);

		this.task$ = task$.pipe(
			withLatestFrom(task$.pipe(switchMap((task) => this.tasksModel.getForm(task ? task.form : {})))),
			map(([task, form]) => {
				if (!task || !task.form) {
					this.ref = null;

					return task;
				}

				if (typeof task.form === "string" && form) {
					const taskWithForm = { ...task, form: { id: task.form, version: form.version } };
					this.ref = taskWithForm;

					return taskWithForm;
				}

				this.ref = null;

				return task;
			}),
		);

		runInInjectionContext(this.injector, () => {
			// @ts-expect-error TS2322
			this.task = toSignal(this.task$);
		});
	}

	private navigateToNotFound() {
		this.router.navigate(["..", "notfound"], { relativeTo: this.route });
	}
}
